import type { FC } from 'react';
import React, { useState } from 'react';

import { Box, COLORS, Text } from '@clutter/clean';

import { MovingFAQ } from '@root/components/www/shared/moving_faq';
import { MovingPreview } from '@root/components/www/shared/moving_preview';
import { MovingProductComparison } from '@root/components/www/shared/moving_product_comparison';
import { ProductOverview } from '@root/components/www/shared/product_overview';

import { Bullets } from '@shared/bullets';
import { ButtonCTA } from '@shared/button_cta';
import { Block } from '@shared/page_builder/block';
import { ContentWithSideImage } from '@shared/page_builder/components/content_with_side_image';
import { IconValueProps } from '@shared/page_builder/components/icon_value_props';
import { ImageHero } from '@shared/page_builder/components/image_hero';
import { ReviewCarousel } from '@shared/page_builder/components/review_carousel';
import { ServiceCards } from '@shared/page_builder/components/service_cards';
import { SideBySide } from '@shared/page_builder/components/side_by_side';
import { SimpleHeadingCTA } from '@shared/page_builder/components/simple_heading_cta';
import { PageBuilder } from '@shared/page_builder/page_builder';
import { HoardersCard } from '@shared/partnerships/hoarders_card';
import { HonestRenovationsCard } from '@shared/partnerships/honest_renovations_card';
import { InstantDreamHomeCard } from '@shared/partnerships/intant_dream_home_card';
import { RachaelRayShowCard } from '@shared/partnerships/rachael_ray_show_card';
import { SwedishDeathCleaningCard } from '@shared/partnerships/swedish_death_cleaning_card';
import { RoundedResponsiveImage } from '@shared/rounded_responsive_image';
import { ServiceSelection } from '@shared/service_selector/constants';
import { TabContainer } from '@shared/tab_container';
import { TabItem } from '@shared/tab_item';
import { JessicaAlbaPlayer } from '@shared/videos/jessica_alba_player';

import calendar from '@images/illustrations/calendar.svg';
import moverWithSixteenFootTruck from '@images/illustrations/mover_with_sixteen_foot_truck.svg';
import moverTruck from '@images/illustrations/mover_with_truck.svg';
import tvMovers from '@images/illustrations/movers_with_tv_small.svg';
import onlineQuote from '@images/illustrations/online_quote_check.svg';
import storageFacility from '@images/illustrations/storage_facility.svg';
import selfStorageFacility from '@images_responsive/home/self-storage-facility.jpg';
import familyWithMovers from '@images_responsive/landing_pages/google/family_with_movers.jpg';
import moverDeliveringBox from '@images_responsive/landing_pages/google/mover_delivering_box.jpg';
import manPackingBox from '@images_responsive/landing_pages/store_and_move/man-packing-box.jpg';
import twoMoversWorkingOnTruck from '@images_responsive/landing_pages/store_and_move/two-movers-working-on-truck.png';
import storageHero from '@images_responsive/locations/storage_hero.jpg';
import appPreview from '@images_responsive/source/app_preview.jpg';
import familyWithBoxes from '@images_responsive/source/family_with_boxes.jpg';
import womanOnTabletWithMovers from '@images_responsive/source/woman_on_tablet_with_movers.jpg';
import couplePackingBoxes from '@images_responsive/www/services/couple_packing_boxes.jpg';
import movingSecondaryImage from '@images_responsive/www/services/mover_unloading_box.jpg';
import movingMainImage from '@images_responsive/www/services/moving_open_window.jpg';
import movingTeam from '@images_responsive/www/services/moving_team.jpg';

const TITLE = 'Apartment Movers: Professional Apartment Moving Services';
const DESCRIPTION =
  'Need reliable apartment movers? Enjoy a smooth and stress-free move with our professional moving services. Local and long-distance options are available. Book your move now!';

const VALUES = [
  {
    title: 'Transparent pricing',
    description:
      'Answer a few questions and we’ll get you a customized quote in under 2 minutes.',
    alt: 'Hands holding a tablet with a checkmark on screen',
    illustration: onlineQuote,
  },
  {
    title: 'Don’t lift a finger',
    description:
      'We’ll bring all the packing supplies needed to efficiently pack and move your items to your new home.',
    alt: 'Clutter crew lifting and moving a TV',
    illustration: tvMovers,
  },
  {
    title: 'Free month of storage included',
    description:
      'One less thing to worry about: get 1 month of storage free when you move with us.',
    alt: 'Clutter’s storage unit with the Clutter logo',
    illustration: storageFacility,
  },
  {
    title: 'Professional team',
    description:
      'Our team is trained to keep your items safe every step of the way.',
    alt: 'Clutter crew moving boxes in a branded Clutter moving truck',
    illustration: moverTruck,
  },
  {
    title: 'Flexible scheduling',
    description:
      'We’re here whenever you need us. Schedule your move on the most convenient day and time for you.',
    alt: 'Calendar with a date circled in red',
    illustration: calendar,
  },
  {
    title: 'We also deliver',
    description:
      'Whenever you need something back, just order a return and Clutter will get it to your doorstep. No storage trips. No hassle.',
    alt: 'Clutter team delivering items on a trolley with a Clutter moving truck in the background',
    illustration: moverWithSixteenFootTruck,
  },
];

const SERVICES = [
  {
    img: selfStorageFacility,
    imgAlt: 'Clutter’s individual storage units with closed shutters',
    title: 'Renting storage space',
    description: (
      <>
        We will pick up and take a photo-inventory of all of your items, move
        them into storage, and deliver items back when you want.
      </>
    ),
  },
  {
    img: storageHero,
    imgAlt: 'Clutter’s team helping pack a sofa set in a home',
    title: 'Furniture storage',
    description: (
      <>
        We’ll carefully wrap, move, and store your furniture in our secure
        facility. Need it back? Just schedule a return - we’ll bring it right to
        your door.
      </>
    ),
  },
  {
    img: womanOnTabletWithMovers,
    imgAlt: 'Clutter’s truck with packed boxes stacked inside and on the curb',
    title: 'Storing between moves',
    description: (
      <>
        We’ll pick up and pack your items, move them into storage, and deliver
        them to your new destination when it’s ready.
      </>
    ),
  },
];

const SITUATIONS = [
  {
    img: twoMoversWorkingOnTruck,
    imgAlt: 'Clutter’s team packing china in a branded box',
    title: 'Last Minute Movers',
    description: (
      <>
        Need to move in a hurry? Clutter is built for speed, offering fast
        booking, quick pickups, and efficient moving services when time isn’t on
        your side. Whether it’s a sudden lease change or a last-minute decision,
        we’ll help you move stress-free, right on schedule.
      </>
    ),
  },

  {
    img: manPackingBox,
    imgAlt: 'Clutter’s team packing china in a branded box',
    title: 'Small Storage',
    description: (
      <>
        Just need a little extra space? Clutter’s small storage options are
        perfect for a few boxes, seasonal items, or anything you don’t use
        daily. With flexible plans, secure storage, and on-demand pickups, you
        can declutter without overcommitting.
      </>
    ),
  },

  {
    img: moverDeliveringBox,
    imgAlt:
      'Clutter’s team handing a branded box to a customer in front of a Clutter truck',
    title: 'Long-Distance Storage',
    description: (
      <>
        Planning a long-distance or cross-country move? Clutter’s got you
        covered with expert packing, secure transport, and even temporary
        storage if you need it. From pickup to final delivery, we make moving
        easy - wherever you’re headed.
      </>
    ),
  },
];

const REVIEWS = [
  {
    title: 'I can’t recommend Clutter enough',
    quote:
      'These folks are phenomenal. They communicate with you before the pickup and the day of the pickup/delivery so you know exactly when they will arrive. They are polite and hard working. I can’t recommend Clutter enough.',
    reviewer: 'Brian F, Los Angeles',
  },
  {
    title: 'Delivered my items with a smile',
    quote:
      'I sent my items off to storage for 8 months due to a house renovation. When I scheduled my drop off, they showed up on time and delivered my items with a smile! I’m very happy I went with Clutter.',
    reviewer: 'Rebecca H, New York',
  },
  {
    title: 'Clutter came to my house',
    quote:
      'I left town to spend some time with my family. Clutter came to my house and helped me finish packing. It took the stress out of needing to leave town so quickly.',
    reviewer: 'Dani D, Seattle',
  },
];

const PARTNERSHIPS = [
  {
    name: 'Netflix',
    Component: InstantDreamHomeCard,
  },
  {
    name: 'Jessica Alba',
    Component: HonestRenovationsCard,
  },
  {
    name: 'Amy Poehler',
    Component: SwedishDeathCleaningCard,
  },
  {
    name: 'Rachael Ray',
    Component: RachaelRayShowCard,
  },
  {
    name: 'Hoarders',
    Component: HoardersCard,
  },
];

const PartnershipsCarousel: FC<{
  background?: string;
}> = ({ background = COLORS.tealBackground }) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const { Component } = PARTNERSHIPS[activeTabIndex];

  return (
    <>
      <Block>
        <Box textAlign="center">
          <Text.Title size="large">
            Trusted by people and brands you know and love...
          </Text.Title>
        </Box>
      </Block>
      <Block>
        <Box.Flex justifyContent="center" alignItems="center" gap={24}>
          <Box.Flex
            flexDirection="column"
            gap={24}
            maxWidth={480}
            minWidth={240}
          >
            <Text.Title size="small">
              “Clutter managed to make the process so simple and convenient”
            </Text.Title>
            <Text.Body>
              Moving is seriously no joke and Clutter managed to make the
              process so simple and convenient for our fam. They packed, moved,
              and unloaded everything we own (and stored what we don’t use
              regularly). 10/10 would recommend Clutter to anyone who is just
              storing a few things or moving their entire lives.
            </Text.Body>
            <Text.Body>
              <strong>— Jessica Alba, Actress</strong>
            </Text.Body>
          </Box.Flex>
          <JessicaAlbaPlayer />
        </Box.Flex>
      </Block>
      <Box style={{ background }}>
        <Block background={background}>
          <Box margin={['32px 0 24px', '48px 0 40px']}>
            <TabContainer>
              {PARTNERSHIPS.map((partnership, index) => (
                <TabItem
                  key={index}
                  name={partnership.name}
                  active={activeTabIndex === index}
                  onClick={() => {
                    setActiveTabIndex(index);
                  }}
                />
              ))}
            </TabContainer>
          </Box>
          <Component />
        </Block>
      </Box>
    </>
  );
};

export const ApartmentMovingPage: FC = () => (
  <PageBuilder title={TITLE} description={DESCRIPTION}>
    <ImageHero
      image={{ ...familyWithBoxes, width: 663, height: 352 }}
      imageAlt="Family of 4 in their apartment surrounded by Clutter packed boxes, while the Clutter team helps them pack"
      service={ServiceSelection.Moving}
    >
      <Box margin={['0 0 32px', null, '0 0 48px']}>
        <Text.Title size="large">Apartment Moving Services</Text.Title>
        <Text.Headline>
          Moving apartments? Our apartment-to-apartment movers handle everything
          — packing, moving, and delivering your belongings safely, so you can
          enjoy a hassle-free move.
        </Text.Headline>
      </Box>
    </ImageHero>

    <ProductOverview
      title="Movers you can trust"
      subtitle="Get a transparent quote online in minutes."
      mainImage={movingMainImage}
      secondaryImage={movingSecondaryImage}
      mainAlt="A happy couple in their apartment, ready to move"
      secondaryAlt="Clutter movers carrying boxes to and from a moving truck"
      service={ServiceSelection.Moving}
      valueProps={[
        'Simple pricing',
        'Packing help & supplies',
        'Truck & professional driver',
        'Efficient & friendly movers',
      ]}
    />

    <PartnershipsCarousel />

    <IconValueProps
      title="Seamless, Stress-Free Apartment Moving"
      subtitle="Clutter’s apartment movers make relocating seamless and stress-free. We handle everything from packing and loading to transport, with clear pricing and flexible scheduling. Get affordable end-to-end apartment moving services you can trust."
      valueProps={VALUES}
      cta={{ service: ServiceSelection.Moving }}
    />

    <SideBySide>
      <RoundedResponsiveImage
        alt="A couple sitting on the floor of their home surrounded by packed Clutter branded boxes"
        image={couplePackingBoxes}
        loading="lazy"
      />
      <Box>
        <Box.Flex flexDirection="column" gap={12} margin="0 0 24px">
          <Text.Title size="large">
            Why Choose Clutter’s Apartment Moving Services
          </Text.Title>
          <Bullets>
            <>Full-service apartment moving company - packing to delivery</>
            <>Transparent cost of moving apartments with no surprises</>
            <>Flexible scheduling with affordable apartment moving options</>
          </Bullets>
        </Box.Flex>
        <ButtonCTA
          container="content"
          label="Get a Quote"
          entryPoint="landing_page_cta"
          service={ServiceSelection.Moving}
        />
      </Box>
    </SideBySide>

    <SideBySide>
      <Box>
        <Box.Flex flexDirection="column" gap={12} margin="0 0 24px">
          <Text.Title size="large">Expertise You Can Rely On</Text.Title>
          <Bullets>
            <>Specialized apartment to apartment movers and condo movers</>
            <>Trusted team of apartment movering specialist</>
          </Bullets>
        </Box.Flex>
        <ButtonCTA
          container="content"
          label="Get a Quote"
          entryPoint="landing_page_cta"
          service={ServiceSelection.Moving}
        />
      </Box>
      <RoundedResponsiveImage
        alt="Three Clutter move and pack experts"
        image={movingTeam}
        loading="lazy"
      />
    </SideBySide>

    <ServiceCards
      title="From Apartment Moves to Secure Storage—We Do It All"
      cards={SERVICES}
    />

    <MovingProductComparison title="Move Better with Clutter" />

    <SimpleHeadingCTA
      label="Check Pricing"
      title="Cheaper than traditional moving, completely stress-free"
      trackingContainer="subhero"
      service={ServiceSelection.Moving}
    />

    <MovingPreview />

    <Block>
      <Box textAlign="center">
        <Text.Title size="large">
          52,000 Happy Customers Served, <br />
          3.4M Items Stored, 99% On-Time
        </Text.Title>
      </Box>
    </Block>

    <ReviewCarousel reviews={REVIEWS} />

    <ContentWithSideImage
      title="A smarter way to store"
      imageA={{
        ...appPreview,
        width: 1449,
        height: 786,
        fit: 'crop',
        crop: 'focalpoint',
        fpY: 0.4,
      }}
      cta={{
        container: 'moving_detail',
        label: 'Get a Quote',
        service: ServiceSelection.Moving,
      }}
      content={[
        {
          headline: 'Secure warehouse',
          description:
            'Our state of the art storage facilities keep your items organized and safe.',
        },
        {
          headline: 'Online photo inventory',
          description:
            'Browse your items with your online photo catalog and have them delivered directly to your home.',
        },
      ]}
      imageAlignment="right"
    />

    <ContentWithSideImage
      title="Affordable, flexible, reliable"
      imageA={familyWithMovers}
      cta={{
        container: 'moving_detail',
        label: 'Get a Quote',
        service: ServiceSelection.Storage,
      }}
      content={[
        {
          headline: 'Low monthly prices',
          description:
            'We offer pricing at or below your local self-storage provider.',
        },
        {
          headline: 'Unbeatable service',
          description:
            'We’ve invested in building a full-time team of moving professionals who are thoroughly vetted to be the best of the best.',
        },
      ]}
      imageAlignment="left"
    />

    <ServiceCards
      title="Moving & Storage for Any Situation"
      cards={SITUATIONS}
    />

    <Block>
      <MovingFAQ />
    </Block>
  </PageBuilder>
);
